import { utrConfigSchema } from '@forgd/shared/universal-token-rating'
import { EngagementOptionEnumValues, MarketMakerKpiTypeEnumValues } from '@forgd/shared/enums'
import { z } from 'zod'

/**
 * Universal Token Rating (UTR) contract types.
 *
 * The index scores each token on two 0–10 axes and multiplies them into a
 * 0–100 score: Disclosure D(t) × Performance P(t).
 */

export const UtrAxisSchema = z.enum(['disclosure', 'performance'])
export type UtrAxis = z.infer<typeof UtrAxisSchema>

export const UtrLeaderboardExchangeTiersSchema = z.object({
  tier1: z.number().int().nonnegative(),
  tier2: z.number().int().nonnegative(),
  tier3: z.number().int().nonnegative(),
  dex: z.number().int().nonnegative(),
})
export type UtrLeaderboardExchangeTiers = z.infer<typeof UtrLeaderboardExchangeTiersSchema>

export const UtrCategoryScoreSchema = z.object({
  axis: UtrAxisSchema,
  category: z.string(),
  // Sum of the category's weight-adjusted criterion scores.
  weightedScore: z.number(),
  // weightedScore / (category weight × 10) → 0–1, comparable across categories.
  percentageScore: z.number(),
  // Rank of percentageScore within the returned rated projects, 0–1.
  percentile: z.number(),
  // Letter grade from the category's score on the grading scale; null when not scored.
  grade: z.string().nullable(),
})
export type UtrCategoryScore = z.infer<typeof UtrCategoryScoreSchema>

export const UtrLeaderboardTokenSchema = z.object({
  // Canonical Arctic project id.
  id: z.string(),
  arcticId: z.string(),
  slug: z.string(),
  coingeckoId: z.string().nullish(),
  cmcId: z.string().nullish(),
  name: z.string(),
  ticker: z.string().nullable(),
  image: z.string().nullish(),
  rank: z.number().int().nullish(),
  grade: z.string().nullish(),
  notRated: z.boolean(),
  tgeStatus: z.enum(['pre', 'post']),
  verified: z.boolean(),
  sector: z.string().nullish(),
  fdv: z.number().nullish(),
  score: z.number(),
  disclosureScore: z.number(),
  performanceScore: z.number(),
  // Month-over-month index change (%).
  momChangePct: z.number(),
  exchangeTiers: UtrLeaderboardExchangeTiersSchema,
  categories: z.array(UtrCategoryScoreSchema),
})
export type UtrLeaderboardToken = z.infer<typeof UtrLeaderboardTokenSchema>

export const UtrTopMoversResponseSchema = z.object({
  topMovers: z.array(
    z.object({
      name: z.string(),
      imageUrl: z.string().nullish(),
      momChangePct: z.number(),
    }),
  ),
})
export type UtrTopMoversResponse = z.infer<typeof UtrTopMoversResponseSchema>

export const UtrLeaderboardResponseSchema = z.object({
  configVersion: z.number().int(),
  generatedAt: z.string().datetime(),
  scoredCount: z.number().int(),
  totalCount: z.number().int(),
  tokens: z.array(UtrLeaderboardTokenSchema),
})
export type UtrLeaderboardResponse = z.infer<typeof UtrLeaderboardResponseSchema>

export const UtrAssetSearchResultSchema = z.object({
  id: z.string(),
  projectName: z.string(),
  symbol: z.string(),
  isActive: z.boolean(),
  isUtr: z.boolean(),
  coingeckoId: z.string().nullable(),
  cmcId: z.string().nullable(),
  image: z.string().nullable(),
})
export type UtrAssetSearchResult = z.infer<typeof UtrAssetSearchResultSchema>

const UtrGradeSchema = z.string().nullable()
const UtrRatingFooterSchema = z.object({
  criterionKey: z.string(),
  score: z.number().nullish(),
  grade: UtrGradeSchema.optional(),
  percentile: z.number().nullish(),
})

export const UtrCriterionBreakdownSchema = z.object({
  key: z.string(),
  label: z.string(),
  weight: z.number(),
  raw: z.number().nullish(),
  rawFormat: z.string().nullish(),
  score: z.number(),
  grade: UtrGradeSchema.optional(),
  percentile: z.number().nullish(),
  note: z.string().nullish(),
  scoredAt: z.string().datetime().nullish(),
})
export type UtrCriterionBreakdown = z.infer<typeof UtrCriterionBreakdownSchema>

export const UtrCategoryBreakdownSchema = z.object({
  key: z.string(),
  label: z.string(),
  score: z.number(),
  grade: UtrGradeSchema,
  percentile: z.number().nullable(),
  criteria: z.array(UtrCriterionBreakdownSchema),
})

export const UtrAxisBreakdownSchema = z.object({
  score: z.number(),
  grade: UtrGradeSchema,
  percentile: z.number().nullable(),
  momChangePct: z.number().nullish(),
  categories: z.array(UtrCategoryBreakdownSchema),
})

export const UtrSnapshotMetricsSchema = z.object({
  marketCapUsd: z.number().nullish(),
  fdvUsd: z.number().nullish(),
  spotVolume24hUsd: z.number().nullish(),
  cexVolume24hUsd: z.number().nullish(),
  dexVolume24hUsd: z.number().nullish(),
  spotDepthUsd: z.number().nullish(),
  cexDepthUsd: z.number().nullish(),
  dexDepthUsd: z.number().nullish(),
  vwasBps: z.number().nullish(),
  greenCandles30d: z.number().nullish(),
  totalCandles30d: z.number().nullish(),
  maxGreenStreak30d: z.number().nullish(),
  openInterestUsd: z.number().nullish(),
  fundingRateBps: z.number().nullish(),
  perpVolume24hUsd: z.number().nullish(),
  perpDepthUsd: z.number().nullish(),
  mmVolumePct: z.number().nullish(),
  mmDepthPct: z.number().nullish(),
  mmUptimePct: z.number().nullish(),
  mmAvgSpread: z.number().nullish(),
  tier1VenueCount: z.number().int(),
  tier2VenueCount: z.number().int(),
  tier3VenueCount: z.number().int(),
  dexVenueCount: z.number().int(),
  annualizedInflationPct: z.number().nullish(),
  releasedSupplyTokens: z.number().nullish(),
  cumulativeBuybackTokens: z.number().nullish(),
  cumulativeBurnTokens: z.number().nullish(),
  vestingAmountVariancePct: z.number().nullish(),
  vestingTimingVarianceDays: z.number().nullish(),
  undisclosedUnlockDetected: z.boolean().nullable(),
  hasVestingDisclosure: z.boolean().nullable(),
})

const UtrDatedPointSchema = z.object({ date: z.string().date() })
const UtrMarketQualityChartSchema = z.object({
  windowDays: z.number().int(),
  series: z.array(
    UtrDatedPointSchema.extend({
      marketCapUsd: z.number().nullish(),
      tradeVolumeUsd: z.number().nullish(),
      depthUsd: z.number().nullish(),
    }),
  ),
  volumeMcap: UtrRatingFooterSchema.extend({
    avgUsd: z.number().nullish(),
    avgMarketCapUsd: z.number().nullish(),
    ratioPct: z.number().nullish(),
  }),
  depthMcap: UtrRatingFooterSchema.extend({
    avgUsd: z.number().nullish(),
    avgMarketCapUsd: z.number().nullish(),
    ratioPct: z.number().nullish(),
  }),
})
const UtrSpreadExchangeLineSchema = z.object({
  exchangeId: z.string(),
  displayName: z.string(),
  logoUrl: z.string().nullish(),
  points: z.array(UtrDatedPointSchema.extend({ spreadPct: z.number() })),
})
const UtrFundingExchangeLineSchema = z.object({
  exchangeId: z.string(),
  displayName: z.string(),
  logoUrl: z.string().nullish(),
  points: z.array(UtrDatedPointSchema.extend({ fundingPct: z.number() })),
})
const UtrCexDexChartSchema = UtrRatingFooterSchema.extend({
  windowDays: z.number().int(),
  points: z.array(UtrDatedPointSchema.extend({ cexUsd: z.number(), dexUsd: z.number() })),
  cexAvgUsd: z.number(),
  dexAvgUsd: z.number(),
  cexSharePct: z.number().nullish(),
})
const UtrSpotPerpChartSchema = UtrRatingFooterSchema.extend({
  windowDays: z.number().int(),
  points: z.array(UtrDatedPointSchema.extend({ spotUsd: z.number(), perpsUsd: z.number() })),
  spotAvgUsd: z.number(),
  perpsAvgUsd: z.number(),
  spotSharePct: z.number().nullish(),
})

export const UtrAssetChartsSchema = z.object({
  marketQuality: UtrMarketQualityChartSchema.nullish(),
  bidAskSpread: UtrRatingFooterSchema.extend({
    windowDays: z.number().int(),
    exchanges: z.array(UtrSpreadExchangeLineSchema),
    tightestExchange: z.string().nullish(),
    vwasPct: z.number().nullish(),
  }).nullish(),
  pricePerformance: UtrRatingFooterSchema.extend({
    windowDays: z.number().int(),
    candles: z.array(
      UtrDatedPointSchema.extend({
        openUsd: z.number(),
        highUsd: z.number(),
        lowUsd: z.number(),
        closeUsd: z.number(),
      }),
    ),
    changePct: z.number().nullish(),
    greenCandles: z.number().nullish(),
    redCandles: z.number().nullish(),
    winRatePct: z.number().nullish(),
  }).nullish(),
  openInterest: UtrRatingFooterSchema.extend({
    windowDays: z.number().int(),
    venues: z.array(
      z.object({
        exchangeId: z.string(),
        displayName: z.string(),
        logoUrl: z.string().nullish(),
        avgOiUsd: z.number(),
      }),
    ),
    totalOiUsd: z.number(),
    oiMcapRatioPct: z.number().nullish(),
  }).nullish(),
  fundingRate: UtrRatingFooterSchema.extend({
    windowDays: z.number().int(),
    exchanges: z.array(UtrFundingExchangeLineSchema),
    oiWeightedFundingPct: z.number().nullish(),
  }).nullish(),
  cexVsDexVolume: UtrCexDexChartSchema.nullish(),
  cexVsDexDepth: UtrCexDexChartSchema.nullish(),
  spotVsPerpsVolume: UtrSpotPerpChartSchema.nullish(),
  spotVsPerpsDepth: UtrSpotPerpChartSchema.nullish(),
})

export const UtrPreTgeSectionSchema = z.object({
  fundraisingRounds: z.array(
    z.object({
      round: z.string(),
      amountUsd: z.number(),
    }),
  ),
  totalFundraisedUsd: z.number().nullish(),
  investorTiers: z.array(
    z.object({
      tier: z.number().int(),
      investorCount: z.number().int(),
      amountRaisedUsd: z.number(),
    }),
  ),
  tier1RaisedUsd: z.number().nullish(),
  tier1RaisedPct: z.number().nullish(),
  pastRoadmapAchievedOnTime: z.boolean().nullish(),
  coreSoftwareOpen: z.boolean().nullish(),
  smartContractAuditsPublic: z.boolean().nullish(),
  revenueGeneratedUsd: z.number().nullish(),
  communityInterest: z.number().int().nullish(),
  communityDistribution: z.number().int().nullish(),
  communityRetentionPct: z.number().nullish(),
})
export type UtrPreTgeSection = z.infer<typeof UtrPreTgeSectionSchema>

const UtrMarketMakerLeaderboardEntrySchema = z.object({
  rank: z.number().int().nullish(),
  percentile: z.number().nullish(),
})

const UtrMarketMakerExchangeDepthSchema = z.object({
  exchangeId: z.string(),
  exchangeName: z.string(),
  ask50: z.number(),
  ask50Percentage: z.number(),
  ask100: z.number(),
  ask100Percentage: z.number(),
  ask200: z.number(),
  ask200Percentage: z.number(),
  bid50: z.number(),
  bid50Percentage: z.number(),
  bid100: z.number(),
  bid100Percentage: z.number(),
  bid200: z.number(),
  bid200Percentage: z.number(),
  bidAsk50: z.number(),
  bidAsk50Percentage: z.number(),
  bidAsk100: z.number(),
  bidAsk100Percentage: z.number(),
  bidAsk200: z.number(),
  bidAsk200Percentage: z.number(),
})

const UtrMarketMakerExchangeVolumeSchema = z.object({
  exchangeId: z.string(),
  exchangeName: z.string(),
  taker: z.number(),
  takerPercentage: z.number(),
  maker: z.number(),
  makerPercentage: z.number(),
  makerTaker: z.number(),
  makerTakerPercentage: z.number(),
})

export const UtrMarketMakerSummarySchema = z.object({
  name: z.string(),
  engagementType: z.enum(EngagementOptionEnumValues),
  avgFillVolumeTotal: z.number(),
  avgFillVolumeMaker: z.number(),
  avgFillVolumeTaker: z.number(),
  avgBidDepth50: z.number(),
  avgAskDepth50: z.number(),
  avgSpread: z.number().nullish(),
  avgBidAskDepth50: z.number().nullable(),
  avgBidDepth100: z.number(),
  avgAskDepth100: z.number(),
  avgBidAskDepth100: z.number().nullable(),
  avgBidDepth200: z.number(),
  avgAskDepth200: z.number(),
  avgBidAskDepth200: z.number().nullable(),
  // Null when the MM engagement has no loan on record — mirrors quant and the MM-monitoring contract.
  avgVolumeBasedLoanUtil: z.number().nullish(),
  avgLiquidityBasedLoanUtil: z.number().nullish(),
  totalDaysTracked: z.number().int(),
  loanSize: z.number().nullish(),
  overreportingFillVolumeMaker: z.boolean(),
  overreportingFillVolumeTaker: z.boolean(),
  overreportingAskDepth200: z.boolean(),
  overreportingBidDepth200: z.boolean(),
  exchangesActivity: z.object({
    activeDex: z.number().int(),
    activeCex: z.number().int(),
  }),
  percentageOfTotalVolume: z.number(),
  percentageOfTotalLiquidity: z.number(),
  activeEngagementsCount: z.number().int(),
  averageFDV: z.number(),
  depthByExchange: z.array(UtrMarketMakerExchangeDepthSchema),
  fillVolumeByExchange: z.array(UtrMarketMakerExchangeVolumeSchema),
  leaderboards: z.object({
    aggregated: UtrMarketMakerLeaderboardEntrySchema,
    depth: UtrMarketMakerLeaderboardEntrySchema,
    volume: UtrMarketMakerLeaderboardEntrySchema,
    spread: UtrMarketMakerLeaderboardEntrySchema,
    kpi: UtrMarketMakerLeaderboardEntrySchema,
  }),
})
export type UtrMarketMakerSummary = z.infer<typeof UtrMarketMakerSummarySchema>

const UtrMarketMakerPerformanceAverageSchema = z.object({
  marketMakers: z.number().nullish(),
  allOthers: z.number().nullish(),
  total: z.number().nullish(),
  marketMakersPercentage: z.number().nullish(),
  allOthersPercentage: z.number().nullish(),
})

const UtrMarketMakerPerformanceDailySchema = UtrMarketMakerPerformanceAverageSchema.extend({
  date: z.string().date(),
  hasMarketTotals: z.boolean(),
})

const UtrMarketMakerPerformanceSeriesSchema = z.object({
  name: z.string(),
  avgValue: z.number().nullish(),
  avgPercentage: z.number().nullish(),
  dailyBreakdown: z.array(
    z.object({
      date: z.string().date(),
      value: z.number().nullish(),
      percentage: z.number().nullish(),
    }),
  ),
})

export const UtrMarketMakerPerformanceBreakdownSchema = z.object({
  avgBreakdown: UtrMarketMakerPerformanceAverageSchema,
  dailyBreakdown: z.array(UtrMarketMakerPerformanceDailySchema),
  isOverreporting: z.boolean(),
  series: z.array(UtrMarketMakerPerformanceSeriesSchema),
})
export type UtrMarketMakerPerformanceBreakdown = z.infer<typeof UtrMarketMakerPerformanceBreakdownSchema>

const UtrMarketMakerDepthBreakdownsSchema = z.object({
  depth50: UtrMarketMakerPerformanceBreakdownSchema,
  depth100: UtrMarketMakerPerformanceBreakdownSchema,
  depth200: UtrMarketMakerPerformanceBreakdownSchema,
})

export const UtrMarketMakerPerformanceMatrixSchema = z.object({
  depth: z.object({
    bidAsk: UtrMarketMakerDepthBreakdownsSchema,
    bid: UtrMarketMakerDepthBreakdownsSchema,
    ask: UtrMarketMakerDepthBreakdownsSchema,
  }),
  volume: z.object({
    total: UtrMarketMakerPerformanceBreakdownSchema,
    maker: UtrMarketMakerPerformanceBreakdownSchema,
    taker: UtrMarketMakerPerformanceBreakdownSchema,
  }),
})
export type UtrMarketMakerPerformanceMatrix = z.infer<typeof UtrMarketMakerPerformanceMatrixSchema>

export const UtrMarketMakerUptimeSchema = z.object({
  name: z.string(),
  engagementType: z.enum(EngagementOptionEnumValues),
  kpiEngagementType: z.enum(MarketMakerKpiTypeEnumValues),
  uptimeObligation: z.number(),
  performance: z.object({
    avgBidAskSpread: z.number().nullish(),
    avgDepth50: z.number().nullish(),
    avgDepth100: z.number().nullish(),
    avgDepth200: z.number().nullish(),
    avgUptime: z.number().nullish(),
  }),
  breakdown: z.array(
    z.object({
      date: z.string().date(),
      reportedDepth50: z.number().nullish(),
      reportedDepth100: z.number().nullish(),
      reportedDepth200: z.number().nullish(),
      reportedSpread: z.number().nullish(),
      targetDepth50: z.number().nullish(),
      targetDepth100: z.number().nullish(),
      targetDepth200: z.number().nullish(),
      targetSpread: z.number().nullish(),
      achievedDepth50: z.boolean().nullish(),
      achievedDepth100: z.boolean().nullish(),
      achievedDepth200: z.boolean().nullish(),
      achievedSpread: z.boolean().nullish(),
      uptimeRatio: z.number().nullish(),
    }),
  ),
})
export type UtrMarketMakerUptime = z.infer<typeof UtrMarketMakerUptimeSchema>

export const UtrAssetResponseSchema = z.object({
  arcticId: z.string(),
  projectName: z.string(),
  symbol: z.string(),
  description: z.string().nullish(),
  sector: z.string().nullish(),
  chain: z.string().nullish(),
  chainLogoUrl: z.string().nullish(),
  contractAddress: z.string().nullish(),
  websiteUrl: z.string().nullish(),
  hasFundraising: z.boolean().nullish(),
  hasMmData: z.boolean(),
  marketMakers: z.array(UtrMarketMakerSummarySchema).nullable().default(null),
  performanceBreakdown: UtrMarketMakerPerformanceMatrixSchema.nullable().default(null),
  uptimeCalculations: z.array(UtrMarketMakerUptimeSchema).nullable().default(null),
  tgeStatus: z.enum(['pre', 'post']),
  coingeckoId: z.string().nullish(),
  cmcId: z.string().nullish(),
  image: z.string().nullish(),
  verified: z.boolean(),
  notRated: z.boolean(),
  rank: z.number().int().nullish(),
  ratedCount: z.number().int(),
  totalCount: z.number().int(),
  overallScore: z.number().nullish(),
  overallPercentile: z.number().nullish(),
  grade: UtrGradeSchema.optional(),
  momChangePct: z.number().nullish(),
  fdvUsd: z.number().nullish(),
  asOf: z.string().datetime().nullish(),
  lastEditedAt: z.string().datetime().nullish(),
  gradedCount: z.number().int(),
  criteriaCount: z.number().int(),
  disclosure: UtrAxisBreakdownSchema.nullish(),
  performance: UtrAxisBreakdownSchema.nullish(),
  // Null post-TGE, before any pre-TGE facts are entered, or from an older quant.
  preTge: UtrPreTgeSectionSchema.nullable().default(null),
  metrics: UtrSnapshotMetricsSchema.nullish(),
  charts: UtrAssetChartsSchema,
  configVersion: z.number().int(),
  generatedAt: z.string().datetime(),
})
export type UtrAssetResponse = z.infer<typeof UtrAssetResponseSchema>

export const UtrAssetExchangeSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  logoUrl: z.string().nullish(),
})

export const UtrAssetExchangeVenueSchema = z.object({
  venue: z.string(),
  displayName: z.string(),
  logoUrl: z.string().nullish(),
  category: z.string(),
  isDex: z.boolean(),
  pairs: z.number().int(),
  volume24hUsd: z.number().nullish(),
  spreadPct: z.number().nullish(),
  trustScore: z.number().nullish(),
  isStale: z.boolean(),
  isAnomaly: z.boolean(),
  lowTrust: z.boolean(),
  flagged: z.boolean(),
  lastTradedAt: z.string().nullish(),
  openInterestUsd: z.number().nullish(),
  fundingRate: z.number().nullish(),
})

export const UtrAssetExchangeTierSchema = z.object({
  tier: z.enum(['tier_1', 'tier_2', 'tier_3', 'dex']),
  exchanges: z.array(UtrAssetExchangeSchema),
})

export const UtrAssetExchangeDetailSchema = z.object({
  tier: z.enum(['tier_1', 'tier_2', 'tier_3', 'dex']),
  cleanVolume24hUsd: z.number(),
  venues: z.array(UtrAssetExchangeVenueSchema),
})

export const UtrAssetExchangesResponseSchema = z.object({
  arcticId: z.string(),
  symbol: z.string().nullish(),
  asOf: z.string().nullish(),
  configVersion: z.number().int(),
  tiers: z.array(UtrAssetExchangeTierSchema),
  details: z.array(UtrAssetExchangeDetailSchema),
})
export type UtrAssetExchangesResponse = z.infer<typeof UtrAssetExchangesResponseSchema>

export const UtrConfigResponseSchema = z.object({
  config: utrConfigSchema,
})
export type UtrConfigResponse = z.infer<typeof UtrConfigResponseSchema>
